/**
 * Per-company speaker enrollment management (Phase 4 follow-up of speaker-recognition revamp).
 *
 * View of every Speaker entity in the company, with their voice sample history
 * (which meeting it came from, when, and the `confirmed_by` source —
 * voiceprint-auto / llm-introduction / user-confirmed / user email). Each sample
 * can be revoked individually if it was a bad enrollment. Reachable by platform
 * super-admins and by company owners/admins (see `requireCompanyManager`); the
 * backend enforces the same rule on the revoke endpoint.
 */

import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { ActionIcon, Anchor, Badge, Button, Container, Group, Stack, Table, Text, Title } from "@mantine/core"
import { notifications } from "@mantine/notifications"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute, Link } from "@tanstack/react-router"
import { useState } from "react"
import { LineListSkeleton } from "../../../components/PageSkeletons"
import { api as apiClient } from "../../../lib/api"
import { extractErrorMessage } from "../../../lib/api/errors"
import { notifyError } from "../../../lib/notify"
import { queryKeys } from "../../../lib/query-keys"
import { requireCompanyManager } from "../../../lib/route-guards"
import { describeEnrollmentSource } from "../../../lib/speaker-enrollment-source"

interface ISpeakerRow {
  id: string
  name: string
  email: string | null
  title: string | null
}

interface IEnrollmentRow {
  id: string
  source_meeting_id: string | null
  meeting_subject: string | null
  meeting_started_at: string | null
  display_name: string | null
  diarization_label: string | null
  confirmed_by: string | null
  confidence: number | null
  model_version: string
  created_at: string
}

function CompanySpeakersPage(): React.JSX.Element {
  const { id } = Route.useParams()
  const queryClient = useQueryClient()
  const { i18n } = useLingui()
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  // biome-ignore lint/suspicious/noExplicitAny: Eden Treaty dynamic path
  const company = (apiClient.api.companies as any)({ companyId: id }) // eslint-disable-line @typescript-eslint/no-explicit-any

  const { data: speakers, isLoading } = useQuery({
    queryKey: queryKeys.companySpeakers(id),
    queryFn: async (): Promise<ISpeakerRow[]> => {
      const res = await company.speakers.get()
      if (res.error) throw new Error(extractErrorMessage(res.error))
      return (res.data?.data ?? []) as ISpeakerRow[]
    },
  })

  const { mutate: revoke } = useMutation({
    mutationFn: async ({ speakerId, embeddingId }: { speakerId: string; embeddingId: string }) => {
      const res = await company.speakers({ speakerId }).enrollments({ embeddingId }).delete()
      if (res.error) throw new Error(extractErrorMessage(res.error))
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.speakerEnrollments(id, variables.speakerId) })
      notifications.show({
        title: i18n._(msg`Sample revoked`),
        message: i18n._(msg`The voice sample has been deleted.`),
        color: "success",
      })
    },
    onError: (err) => notifyError(err),
  })

  return (
    <Container size="lg" py="md">
      <Group justify="space-between" mb="lg">
        <Group>
          <Anchor renderRoot={(props) => <Link to="/companies/$id" params={{ id }} {...props} />} size="sm">
            &larr; <Trans>Company</Trans>
          </Anchor>
          <Title order={2}>
            <Trans>Speaker enrollments</Trans>
          </Title>
        </Group>
      </Group>

      <Text c="dimmed" mb="md" size="sm">
        <Trans>
          Each row is a known voice for this company. Expand a row to see every voiceprint sample that has been
          collected for them, the meeting it originated from, and how it was confirmed. Bad samples can be revoked
          individually — the speaker stays, only the misattributed voice sample is deleted.
        </Trans>
      </Text>

      {isLoading && <LineListSkeleton rows={4} />}

      {!isLoading && (speakers?.length ?? 0) === 0 && (
        <Text c="dimmed" ta="center" py="xl">
          <Trans>No speakers enrolled yet for this company.</Trans>
        </Text>
      )}

      <Stack gap="xs">
        {speakers?.map((s) => (
          <SpeakerCard
            key={s.id}
            companyId={id}
            speaker={s}
            expanded={expanded.has(s.id)}
            onToggle={() =>
              setExpanded((prev) => {
                const next = new Set(prev)
                if (next.has(s.id)) next.delete(s.id)
                else next.add(s.id)
                return next
              })
            }
            onRevoke={(embeddingId) => revoke({ speakerId: s.id, embeddingId })}
          />
        ))}
      </Stack>
    </Container>
  )
}

interface ISpeakerCardProps {
  companyId: string
  speaker: ISpeakerRow
  expanded: boolean
  onToggle: () => void
  onRevoke: (embeddingId: string) => void
}

function SpeakerCard({ companyId, speaker, expanded, onToggle, onRevoke }: ISpeakerCardProps): React.JSX.Element {
  const { i18n } = useLingui()
  // biome-ignore lint/suspicious/noExplicitAny: Eden Treaty dynamic path
  const company = (apiClient.api.companies as any)({ companyId }) // eslint-disable-line @typescript-eslint/no-explicit-any

  const {
    data: enrollments,
    isLoading,
    error: enrollmentsError,
  } = useQuery({
    queryKey: queryKeys.speakerEnrollments(companyId, speaker.id),
    queryFn: async (): Promise<IEnrollmentRow[]> => {
      const res = await company.speakers({ speakerId: speaker.id }).enrollments.get()
      if (res.error) throw new Error(extractErrorMessage(res.error))
      return (res.data?.data ?? []) as IEnrollmentRow[]
    },
    enabled: expanded,
  })

  return (
    <Stack
      gap={0}
      p="sm"
      style={{
        border: "1px solid var(--mantine-color-default-border)",
        borderRadius: "var(--mantine-radius-sm)",
      }}
    >
      <Group justify="space-between" wrap="nowrap">
        <Group gap="sm">
          <Text fw={600}>{speaker.name}</Text>
          {speaker.title && (
            <Text c="dimmed" size="sm">
              {speaker.title}
            </Text>
          )}
          {speaker.email && (
            <Text c="dimmed" size="xs" ff="monospace">
              {speaker.email}
            </Text>
          )}
        </Group>
        <Button variant="subtle" size="xs" onClick={onToggle}>
          {expanded ? <Trans>Hide samples</Trans> : <Trans>Show samples</Trans>}
        </Button>
      </Group>

      {expanded && (
        <Stack gap="xs" mt="md">
          {isLoading && <LineListSkeleton rows={3} />}
          {!isLoading && enrollmentsError && (
            <Text c="danger" size="sm">
              <Trans>Couldn't load voice samples: {extractErrorMessage(enrollmentsError)}</Trans>
            </Text>
          )}
          {!isLoading && !enrollmentsError && (enrollments?.length ?? 0) === 0 && (
            <Text c="dimmed" size="sm">
              <Trans>No voiceprint samples yet — this speaker has been named but never voice-enrolled.</Trans>
            </Text>
          )}
          {!isLoading && (enrollments?.length ?? 0) > 0 && (
            <Table highlightOnHover withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>
                    <Trans>Source</Trans>
                  </Table.Th>
                  <Table.Th>
                    <Trans>Meeting</Trans>
                  </Table.Th>
                  <Table.Th>
                    <Trans>Label</Trans>
                  </Table.Th>
                  <Table.Th>
                    <Trans>Enrolled at</Trans>
                  </Table.Th>
                  <Table.Th style={{ width: 60 }} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {enrollments?.map((e) => {
                  const src = describeEnrollmentSource(e.confirmed_by, i18n)
                  return (
                    <Table.Tr key={e.id}>
                      <Table.Td>
                        <Badge color={src.color} variant="light" title={src.tooltip}>
                          {src.label}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{e.meeting_subject ?? <span style={{ opacity: 0.6 }}>—</span>}</Text>
                        {e.source_meeting_id && (
                          <Text size="xs" c="dimmed" ff="monospace">
                            {e.source_meeting_id}
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs" ff="monospace">
                          {e.diarization_label ?? "—"}
                        </Text>
                        {e.display_name && e.display_name !== speaker.name && (
                          <Text size="xs" c="dimmed">
                            <Trans>(displayed as {e.display_name})</Trans>
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs">{e.created_at.split("T")[0]}</Text>
                      </Table.Td>
                      <Table.Td>
                        <ActionIcon
                          color="danger"
                          variant="subtle"
                          size="sm"
                          title={i18n._(msg`Revoke this voice sample`)}
                          aria-label={i18n._(msg`Revoke sample`)}
                          onClick={() => {
                            if (
                              window.confirm(i18n._(msg`Revoke this voice sample? It will be permanently deleted.`))
                            ) {
                              onRevoke(e.id)
                            }
                          }}
                        >
                          ✕
                        </ActionIcon>
                      </Table.Td>
                    </Table.Tr>
                  )
                })}
              </Table.Tbody>
            </Table>
          )}
        </Stack>
      )}
    </Stack>
  )
}

export const Route = createFileRoute("/_auth/companies/$id_/speakers")({
  beforeLoad: requireCompanyManager,
  component: CompanySpeakersPage,
})
